import { MantineProvider } from '@mantine/core';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

// Mantine's stylesheets must come before globals.css, or the token overrides land under
// the library's own layer and the surface colours silently do nothing.
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
// `@mantine/dates/styles.css` is NOT imported here: it now travels with the lazy chunk(s) that
// render `DateInput` (see `pages/games/GameEditorPage.tsx`), so it stops shipping in the entry
// bundle for pages that never show a date field.
// The Mantine theme and webfonts are the backoffice's own, with the hub's card surface on top
// (`styles/hubTheme.ts`). `globals.css` stays local: it's config-hub's own.
import '@backoffice-client/styles/fonts.css';
import './styles/globals.css';

import { App } from './App.js';
import { AuthProvider } from './auth/index.js';
import { AppConfigProvider } from './config/AppConfig.js';
import { rgsCssVariablesResolver } from '@backoffice-client/styles/theme.js';
import { hubTheme } from './styles/hubTheme.js';
import { reloadForStaleBuild } from './utils/staleBuild.js';

// A lazy chunk that is not on the pod that answered — mid-rollout, or a tab open across a
// deploy. Nothing has rendered from it yet, so reload; the entry bundle's equivalent is the
// inline script in index.html. See `utils/staleBuild.ts`.
window.addEventListener('vite:preloadError', (event) => {
    if (reloadForStaleBuild()) event.preventDefault();
});

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
    <StrictMode>
        <MantineProvider theme={hubTheme} cssVariablesResolver={rgsCssVariablesResolver} defaultColorScheme="light">
            <AppConfigProvider>
                <AuthProvider>
                    <App />
                </AuthProvider>
            </AppConfigProvider>
        </MantineProvider>
    </StrictMode>,
);
